import { useNav } from '@/app/features/profile/NavContext'
import ReportEditModeToggle from '@/app/features/reports/components/edit-mode-toggle'
import { ReportSectionProvider, useReportSectionPage } from '@/app/features/reports/components/report-section-context'
import { useReportViewContext } from '@/app/features/reports/components/report-viewer-context'
import { SummaryHighlightsSection } from '@/app/features/reports/components/summary-highlights'
import { ReportTestItem } from '@/app/features/reports/utils/entities'
import BusyButton from '@/components/raytd/busy-button'
import ErrorState from '@/components/raytd/error-state'
import LoadingState from '@/components/raytd/loading-state'
import { SubPageLayout } from '@/components/raytd/sub-page-layout'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { PlateController } from '@udecode/plate-common/react'
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { ManagedCustomSectionContainer } from './components/custom-section-container'
import { FilterSection } from "./components/filter-section"
import { TestDistributionSection } from "./components/test-distribution-section"
import { ProcessedTestData, processTestData } from './utils/processTestData'
import { ExcludedTestsBadge } from '@/app/features/reports/components/excluded-tests-badge'
import EditModeComponent from '@/app/features/reports/components/edit-component'
import { CustomKPISection } from './components/summary/custom-kpi-section'
import { KPITileConfig } from './types/kpi-types'
import { useUpdateReportMutation } from '@app.raytd.com/store'

interface SummaryEditorProps {
    reportId: number;
}

function SummaryEditor({ reportId }: SummaryEditorProps) {
    const { sectionManager, editMode } = useReportSectionPage();
    const { sections, saveSections, isBusy, isDirty } = sectionManager;

    const {
        rawTestItems,
        report,
        isLoading,
        filters,
        filteredData,
        testSuites,
    } = useReportViewContext();

    const [updateReport] = useUpdateReportMutation();

    const {
        selectedFilters,
        facets,
        unfilteredCounts,
        filtersActive,
        setSelectedFilters,
        updateFilters,
        removeFilter
    } = filters;

    // Initialize custom KPI tiles from report.section_config
    const [customTiles, setCustomTiles] = useState<KPITileConfig[]>(() => {
        const kpiTiles = (report?.section_config as any)?.kpi_tiles;
        return Array.isArray(kpiTiles) ? kpiTiles : [];
    });

    // Update tiles when report changes
    useEffect(() => {
        const kpiTiles = (report?.section_config as any)?.kpi_tiles;
        if (Array.isArray(kpiTiles)) {
            setCustomTiles(kpiTiles);
        }
    }, [report?.section_config]);

    const [kpiTilesDirty, setKpiTilesDirty] = useState(false);

    const reset = useCallback(() => {
        console.log('reset');
    }, []);

    const combinedIsDirty = isDirty || kpiTilesDirty;
    const { AlertDialogComponent } = useUnsavedChanges(combinedIsDirty, reset, false);

    const summaryIncludedData = useMemo(() => {
        if (!filteredData) return [];
        return filteredData.filter(item => item.exclusionReason === 'included');
    }, [filteredData]);

    const processedSummaryIncludedData: ProcessedTestData | null = useMemo(() => {
        if (!summaryIncludedData) return null;
        return processTestData(summaryIncludedData);
    }, [summaryIncludedData]);

    const processedUnfilteredData: ProcessedTestData | null = useMemo(() => {
        if (!rawTestItems) return null;
        return processTestData(rawTestItems as ReportTestItem[]);
    }, [rawTestItems]);

    const { setNavButtons } = useNav();

    const handleKpiTilesUpdate = useCallback((tiles: KPITileConfig[]) => {
        setCustomTiles(tiles);
        setKpiTilesDirty(true);
    }, []);

    const onSubmit = useCallback(async () => {
        console.log('submit', sections);
        try {
            // Save text sections only if they are dirty
            if (isDirty) {
                await saveSections();
            }

            // Save KPI tiles to section_config if dirty
            if (kpiTilesDirty && report) {
                const currentSectionConfig = report.section_config || {};
                await updateReport({
                    id: report.id,
                    section_config: {
                        ...currentSectionConfig,
                        kpi_tiles: customTiles,
                    } as any, // section_config can contain additional properties like kpi_tiles
                }).unwrap();
                setKpiTilesDirty(false);
            }

            toast.success('Page content saved');
        } catch (error) {
            console.error('Error saving sections:', error);
            toast.error('Error saving page content');
        }
    }, [sections, saveSections, isDirty, kpiTilesDirty, report, updateReport, customTiles]);

    useEffect(() => {
        setNavButtons(
            editMode && <BusyButton
                onClick={onSubmit}
                disabled={!combinedIsDirty}
                isBusy={isBusy}
                busyText="Saving..."
            >
                Save
            </BusyButton>
        );

        return () => setNavButtons(null);
    }, [onSubmit, combinedIsDirty, isBusy, setNavButtons, editMode]);

    if (isLoading) return <LoadingState message="Loading summary data..." />
    if (!report) return <ErrorState message="Report data not available." />
    if (!processedSummaryIncludedData || !processedUnfilteredData) return <LoadingState message="Processing summary data..." />
    if (!filters?.facets) return <LoadingState message="Loading filter data..." />

    return (
        <SubPageLayout title="Summary">
            <SubPageLayout.Header>
                <ReportEditModeToggle />
            </SubPageLayout.Header>

            <div className='flex flex-row gap-2 print:hidden'>

                <FilterSection
                    showSearch={false}
                    facets={filters.facets}
                    selectedFilters={selectedFilters}
                    updateFilters={updateFilters}
                    removeFilter={removeFilter}
                    filteredCounts={filters.filteredCounts}
                    unfilteredCounts={filters.unfilteredCounts}
                    defaultImplicitFilters={{ excluded: ['included'] }}
                />

                <div className='flex-1' />
                <EditModeComponent>
                    <ExcludedTestsBadge counts={filters?.filteredCounts?.tests} />
                </EditModeComponent>
            </div>

            <ManagedCustomSectionContainer
                position="summary-after-filter"
            />

            <CustomKPISection
                tiles={customTiles}
                data={summaryIncludedData}
                testSuites={testSuites}
                isEditMode={editMode}
                onUpdate={handleKpiTilesUpdate}
            />

            <SummaryHighlightsSection
                filteredData={processedSummaryIncludedData}
                unfilteredData={processedUnfilteredData}
                filtersActive={filtersActive}
            />

            <div className="print-section-distribution">
                <TestDistributionSection
                    filteredData={processedSummaryIncludedData}
                    unfilteredData={processedUnfilteredData}
                    filtersActive={filtersActive}
                />
            </div>

            <ManagedCustomSectionContainer
                position="summary-after-test-distribution"
            />

            <AlertDialogComponent />
        </SubPageLayout>
    )
}

export default function SummaryPage({ reportId }: { reportId: string }) {
    return (
        <ReportSectionProvider page="summary">
            <Suspense fallback={<LoadingState />}>
                <PlateController>
                    <SummaryEditor reportId={parseInt(reportId)} />
                </PlateController>
            </Suspense>
        </ReportSectionProvider>
    )
}